/**
 * HOT — the companies actually in play right now. The board's 🔥 filter.
 *
 * Gabriel's rule, 2026-10-07: hot is about the last time THEY touched us — they came
 * in, we spoke, they emailed — never the last time we nudged them. How recent that
 * touch has to be depends on where they sit, because each stage has its own normal
 * pace: a new lead goes cold in a week, a contract in legal can sit six weeks and
 * still be live.
 *
 *   new             7 business days since they came in or last wrote
 *   call_scheduled  hot while the call is no more than 2 weeks out; with no call
 *                   ahead (it happened, the card has not moved) it reads as in progress
 *   in_progress     10 days
 *   proposal_sent   3 weeks — where deals get stuck, so the longest leash bar legal
 *   evaluating      3 weeks
 *   agreement       6 weeks
 *
 * The touch is `last_touch_at` (GET /board, getLastTouches in src/db/crm.js): their
 * email, a call that has happened, or the form. The arrival counts too, which is
 * what makes a lead that came in yesterday hot before anyone has replied.
 *
 * A card on HOLD is never hot. "Check back with me next quarter" is a reply, and a
 * recent one, but it is the opposite of in play; the hold is where that answer is
 * recorded, so the hold is what this reads. Closed won and lost are not in play by
 * definition.
 */

const MS_DAY = 86400000;

/** Calendar-day windows per stage. `new` is in business days, below. */
export const HOT_DAYS = {
  in_progress: 10,
  proposal_sent: 21,
  evaluating: 21,
  agreement: 42,
};
export const HOT_NEW_BUSINESS_DAYS = 7;
export const HOT_CALL_AHEAD_DAYS = 14;

/** Mon–Fri days elapsed from `from` to `to`, counting whole days. */
export function businessDaysBetween(from, to) {
  const a = new Date(from);
  const b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b <= a) return 0;
  let days = 0;
  const d = new Date(Date.UTC(a.getUTCFullYear(), a.getUTCMonth(), a.getUTCDate()));
  const end = Date.UTC(b.getUTCFullYear(), b.getUTCMonth(), b.getUTCDate());
  while (d.getTime() < end) {
    d.setUTCDate(d.getUTCDate() + 1);
    const wd = d.getUTCDay();
    if (wd !== 0 && wd !== 6) days++;
  }
  return days;
}

function latest(...dates) {
  let best = null;
  for (const x of dates) {
    if (!x) continue;
    const t = new Date(x).getTime();
    if (Number.isNaN(t)) continue;
    if (best === null || t > best) best = t;
  }
  return best;
}

/**
 * Is this board card hot? `held` is whether the card is under a hold (holdState in
 * ./hold.mjs), passed in so this file needs no chip.
 */
export function isHot(co, { now = Date.now(), held = false } = {}) {
  if (!co || held) return false;
  const stage = co.board_bucket === "prospect" || !co.board_bucket ? co.pipeline_stage : null;
  if (!stage) return false;
  const nowMs = new Date(now).getTime();

  if (stage === "call_scheduled" && co.next_meeting_at) {
    const ahead = new Date(co.next_meeting_at).getTime() - nowMs;
    return ahead <= HOT_CALL_AHEAD_DAYS * MS_DAY;
  }

  // The arrival is a touch: created_at for a first-timer, stage_entered_at for a
  // returning company whose record is years old but came back into New this week.
  const touch = latest(
    co.last_touch_at,
    co.created_at,
    stage === "new" ? co.stage_entered_at : null
  );
  if (touch === null) return false;

  if (stage === "new") return businessDaysBetween(touch, nowMs) <= HOT_NEW_BUSINESS_DAYS;
  const window = HOT_DAYS[stage === "call_scheduled" ? "in_progress" : stage];
  if (!window) return false;
  return nowMs - touch <= window * MS_DAY;
}

/** The button's tooltip: the rule, in his terms. */
export const HOT_TITLE =
  "In play right now: they came in, we spoke or they wrote recently (our nudges don't count). " +
  "New: 7 business days · Call scheduled: call within 2 weeks · In progress: 10 days · " +
  "Pricing sent / Deciding: 3 weeks · Agreement: 6 weeks. Cards on hold are left out.";
